import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import { postBySlug } from './posts';

const post = postBySlug('complete-guide-to-pdf-file-security');

const PostPdfSecurity = () => (
    <BlogLayout post={post}>
        <p>
            People treat PDFs as if they&rsquo;re automatically secure — final, non-editable,
            tamper-resistant. A PDF is a tidy envelope for a document, and that feels
            comforting. But &quot;secure&quot; isn&rsquo;t a single feature, and PDFs have many
            of them — some strong, some mostly theatrical. This guide covers what PDF security
            actually consists of in 2026, what each feature protects, and the checklist for
            sharing sensitive PDFs safely.
        </p>

        <h2>The four layers of PDF security</h2>
        <p>
            PDF security is really four separate concerns bundled together:
        </p>
        <ol>
            <li><strong>Access control</strong> — who can open the file.</li>
            <li><strong>Usage control</strong> — what they can do once they&rsquo;ve opened it.</li>
            <li><strong>Authenticity</strong> — proof that it came from the stated sender.</li>
            <li><strong>Content security</strong> — redaction and sanitization before sending.</li>
        </ol>
        <p>
            Each layer uses different mechanisms, and getting them wrong (or conflating them)
            is where most real-world mistakes happen.
        </p>

        <h2>Layer 1: Access control (passwords)</h2>
        <p>
            PDFs support two types of password:
        </p>
        <ul>
            <li>
                <strong>User password (open password):</strong> required to open the file. The
                PDF content is encrypted with AES-256 in modern PDFs.
            </li>
            <li>
                <strong>Owner password (permissions password):</strong> required to change
                permissions. See layer 2.
            </li>
        </ul>

        <h3>How strong is a PDF password?</h3>
        <p>
            If you use a modern PDF reader (which writes AES-256 encryption) and a long,
            random password, the encryption is genuinely strong — comparable to any other
            AES-256 system. An attacker would have to brute-force the password itself.
        </p>
        <p>
            If your password is <code>Password123</code>, <code>CompanyName2024</code>, or the
            name of a family member, brute-forcing is trivial. Password-cracking software for
            PDFs is freely available and runs at millions of guesses per second on a decent
            GPU.
        </p>
        <p>
            <strong>Practical advice:</strong> use a passphrase of 4 or more random words, or
            a randomly generated string of 16+ characters. Share the password through a
            different channel than the document itself — don&rsquo;t send the password in the
            same email as the PDF.
        </p>

        <h3>Risks with PDF passwords</h3>
        <ul>
            <li>
                If you forget the password, you cannot open the file. There is no recovery
                mechanism.
            </li>
            <li>
                Very old PDF readers may only support RC4 encryption (40-bit or 128-bit), which
                is weak by modern standards. Use a recent tool to add password protection.
            </li>
            <li>
                Metadata (author, title, creation date) is sometimes not encrypted, even when
                the content is. An attacker can still learn the document exists.
            </li>
        </ul>

        <h2>Layer 2: Usage control (permissions)</h2>
        <p>
            The owner password can restrict what a user can do with the open document:
        </p>
        <ul>
            <li>Prevent printing, or restrict it to low-resolution.</li>
            <li>Prevent copying text or images.</li>
            <li>Prevent editing.</li>
            <li>Prevent adding annotations.</li>
            <li>Prevent filling form fields.</li>
        </ul>

        <p>
            Here&rsquo;s the uncomfortable truth: <strong>PDF permissions are mostly
            honour-system</strong>. The PDF viewer is trusted to enforce them. Adobe Acrobat
            and other major viewers do enforce them. But it&rsquo;s a trivial matter to find
            an alternative viewer that ignores the restrictions entirely. Free tools to strip
            PDF permissions are one Google search away.
        </p>
        <p>
            In other words: permissions are useful for conveying <em>intent</em> (&quot;please
            don&rsquo;t print this&quot;) but are not a barrier to someone motivated to
            circumvent them. If you actually need to prevent copying or printing, a PDF
            isn&rsquo;t the right tool — you need DRM (a separate technology that keeps the
            file encrypted even while it&rsquo;s being viewed).
        </p>

        <h2>Layer 3: Authenticity (digital signatures)</h2>
        <p>
            A digital signature on a PDF does two things:
        </p>
        <ul>
            <li>
                <strong>Authenticates the signer.</strong> The signature is tied to the
                signer&rsquo;s cryptographic certificate. If you trust the certificate
                authority, you know who signed.
            </li>
            <li>
                <strong>Proves the document wasn&rsquo;t altered after signing.</strong> Any
                modification to the document after signing invalidates the signature.
            </li>
        </ul>
        <p>
            Digital signatures are strong. They rely on public-key cryptography (RSA or
            ECDSA), and they&rsquo;re the technological basis for legally recognized
            e-signatures in most jurisdictions.
        </p>

        <h3>Digital signatures vs. e-signature services</h3>
        <p>
            Tools like DocuSign and HelloSign are <em>e-signature services</em>. They typically
            embed a visual signature image and record consent through a separate audit trail.
            The PDF itself may or may not have a cryptographic digital signature; the legal
            validity often rests on the service&rsquo;s audit records.
        </p>
        <p>
            A <em>qualified electronic signature</em> (in the EU, under eIDAS) requires a
            cryptographic digital signature with a qualified certificate. This is stronger —
            the PDF alone is self-verifying.
        </p>

        <h3>Validating a signature you receive</h3>
        <p>
            In Adobe Acrobat, open the signed PDF and click the signature panel. You&rsquo;ll
            see whether the signature is valid, whether the document has been changed since
            signing, and the certificate chain. Treat any &quot;signature validity unknown&quot;
            warning seriously — it usually means the signer&rsquo;s certificate isn&rsquo;t
            trusted by your system, and the document&rsquo;s authenticity is not verified.
        </p>

        <h2>Layer 4: Content security (redaction and sanitization)</h2>
        <p>
            This layer is where most real-world PDF security failures happen. The classic
            mistake: you need to redact personal information before sending a document. You
            use a PDF editor to draw black rectangles over the sensitive parts. The document
            <em>looks</em> redacted. You send it.
        </p>
        <p>
            Except the underlying text is still there. It&rsquo;s just hidden under a black
            shape. Anyone who copies text from the PDF — or uses <code>pdftotext</code> or any
            number of free tools — gets the original content back, uncovered.
        </p>

        <h3>Proper redaction</h3>
        <p>
            True redaction removes the underlying content, not just covers it visually. Adobe
            Acrobat has a dedicated Redact tool that:
        </p>
        <ol>
            <li>Lets you select the text or regions to redact.</li>
            <li>Removes the underlying content and replaces it with a solid colour block.</li>
            <li>Re-saves the PDF so the original content is not recoverable.</li>
        </ol>

        <h3>Metadata leaks</h3>
        <p>
            PDFs carry metadata: author name, creation date, software used, sometimes revision
            history, and &mdash; if the PDF was generated from Word &mdash; even tracked changes
            and comments that you thought you&rsquo;d deleted. Before sharing sensitive PDFs:
        </p>
        <ul>
            <li>
                Use &quot;Remove Hidden Information&quot; (Acrobat) or &quot;Sanitize Document&quot;
                (other tools) to strip metadata.
            </li>
            <li>In the source document, check the document properties for author fields.</li>
            <li>If the PDF was generated from Word, accept all tracked changes in the original Word doc before exporting.</li>
        </ul>

        <h3>Embedded files and attachments</h3>
        <p>
            PDFs can have other files attached to them. If you received a PDF that includes a
            spreadsheet as an attachment, the spreadsheet travels with the PDF. Sanitization
            strips these too.
        </p>

        <h2>A checklist for sending sensitive PDFs</h2>
        <ol>
            <li>
                <strong>Redact with a proper redaction tool</strong> — never just draw rectangles.
            </li>
            <li>
                <strong>Sanitize</strong> the document to remove metadata, hidden text, and
                embedded content.
            </li>
            <li>
                <strong>Add a password</strong> if the content warrants it. Use a strong one.
            </li>
            <li>
                <strong>Send the password separately</strong> — different channel, different
                message.
            </li>
            <li>
                <strong>Consider a digital signature</strong> if the receiver needs to verify
                authenticity.
            </li>
            <li>
                <strong>Check the preview</strong>. Open the final PDF in a fresh viewer and
                verify nothing sensitive is visible.
            </li>
            <li>
                <strong>Transport matters too.</strong> Email is rarely encrypted end-to-end.
                For very sensitive files, use an encrypted file-sharing service (Proton Drive,
                Tresorit, Signal for small files).
            </li>
        </ol>

        <h2>Why browser-based tools matter here</h2>
        <p>
            When you process sensitive PDFs, the processing step itself is part of your
            security posture. Uploading a medical record to a random online tool to compress
            it — even if your compression is otherwise secure — defeats the purpose. Browser-based
            tools that run entirely on your device keep the document in one place. Our tools
            (<Link to="/compress-pdf">compress</Link>,{' '}
            <Link to="/pdf-to-image">convert to image</Link>, and the others) are designed
            this way for exactly that reason.
        </p>

        <h2>Summary</h2>
        <p>
            PDF security is four different things. Passwords protect the file at rest.
            Permissions express intent, not enforcement. Digital signatures provide real
            authenticity guarantees. Redaction and sanitization prevent content leaks — and
            are the layer where people trip up most. Treat each layer for what it actually
            does, not what it feels like it does, and you&rsquo;ll avoid almost every common
            mistake.
        </p>
    </BlogLayout>
);

export default PostPdfSecurity;
